import type { ChangeEvent } from 'react';
import { Box, FormHelperText, FormLabel, Group, Input, Loader, Select, Switch } from '@screentone/core';

import {
  CollectionParameterDataTypes,
  GetCollectionParametersQuery,
  useGetCollectionParametersQuery,
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks/useConvertedProperty';
import { CollectionParametersProvider, useCollectionParameter } from './collection-parameters-context';

type Parameter = GetCollectionParametersQuery['getCollectionParameters'][number];

function ParameterDescription({ description }: { description?: string | null }) {
  if (!description) return null;
  return <FormHelperText>{description}</FormHelperText>;
}

function BooleanParameterField({ param, isReadOnly }: { param: Parameter; isReadOnly: boolean }) {
  const { value, setValue } = useCollectionParameter(param.key);
  const checked = typeof value === 'boolean' ? value : false;

  return (
    <Group gap="xs" direction="column" fullWidth>
      <FormLabel label={param.label} labelPosition="right">
        <Switch checked={checked} onChange={() => setValue(!checked)} disabled={param.deprecated || isReadOnly} />
      </FormLabel>
      <ParameterDescription description={param.description} />
    </Group>
  );
}

function EnumParameterField({ param, isReadOnly }: { param: Parameter; isReadOnly: boolean }) {
  const { value, setValue } = useCollectionParameter(param.key);
  const current = typeof value === 'string' ? value : '';
  const options = param.value ?? [];

  return (
    <Group gap="xs" direction="column" fullWidth>
      <FormLabel label={param.label} labelPosition="top" fullWidth />
      <Select
        value={current}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => setValue(e.target.value)}
        disabled={param.deprecated || isReadOnly}
      >
        <option value="">—</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </Select>
      <ParameterDescription description={param.description} />
    </Group>
  );
}

function StringParameterField({ param, isReadOnly }: { param: Parameter; isReadOnly: boolean }) {
  const { value, setValue } = useCollectionParameter(param.key);
  const current = typeof value === 'string' ? value : '';

  return (
    <Group gap="xs" direction="column" fullWidth>
      <FormLabel label={param.label} labelPosition="top" fullWidth />
      <Input
        value={current}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
        disabled={param.deprecated || isReadOnly}
        placeholder={`Enter the value for ${param.label}...`}
      />
      <ParameterDescription description={param.description} />
    </Group>
  );
}

function ParameterField({ param, isReadOnly }: { param: Parameter; isReadOnly: boolean }) {
  if (param.dataType === CollectionParameterDataTypes.Boolean) {
    return <BooleanParameterField param={param} isReadOnly={isReadOnly} />;
  }

  if (param.dataType === CollectionParameterDataTypes.Enum) {
    return <EnumParameterField param={param} isReadOnly={isReadOnly} />;
  }

  return <StringParameterField param={param} isReadOnly={isReadOnly} />;
}

type CollectionParametersProps = {
  isReadOnly?: boolean;
};

function CollectionParameters({ isReadOnly = false }: CollectionParametersProps) {
  const publicationKey = useConvertedProperty() ?? '';
  const { data, error } = useGetCollectionParametersQuery(
    { publicationKey },
    { enabled: !!publicationKey }
  );

  if (data) {
    const visibleParameters = data.getCollectionParameters.filter((p) => p.enabled);

    if (!visibleParameters.length) {
      return null
    }

    return (
      <Box margin={{ vertical: 'md' }}>
        <Box.Title>Collection Parameters</Box.Title>
        <Box.Content>
          <CollectionParametersProvider>
            <Group gap="md" direction="column" fullWidth>
              {visibleParameters.map((param) => (
                <ParameterField key={param.key} param={param} isReadOnly={isReadOnly} />
              ))}
            </Group>
          </CollectionParametersProvider>
        </Box.Content>
      </Box>
    );
  }

  if (error) {
    return (
      <Box margin={{ vertical: 'md' }}>
        <Box.Title>Collection Parameters</Box.Title>
        <Box.Content>
          Could not load collection parameters. Please try again later.
        </Box.Content>
      </Box>
    );
  }

  return (
    <Box margin={{ vertical: 'md' }}>
      <Box.Title>Collection Parameters</Box.Title>
      <Box.Content align="center">
        <Loader size="md" />
      </Box.Content>
    </Box>
  );
}

export default CollectionParameters;
